"use client";

import { useConnectWallet } from "@web3-onboard/react";
import { ethers } from "ethers";
import { FunctionComponent, useCallback, useEffect, useState } from "react";

import { Button } from "@/components";
import { CHAINS } from "@/constants";
import { useChain, useSilverPrice } from "@/hooks";
import { currency, fetch, percentage, transaction } from "@/utils";

import styles from "./Form.module.css";

const MAX_NUM_RETRIES = 3;
const RETRY_INTERVAL = 30000; // 30 seconds

export const Form: FunctionComponent<{
  currentlyDeposited: number | undefined;
  currentInterestRate: number | undefined;
  maxDeposit: number | undefined;
}> = ({ currentlyDeposited, maxDeposit }) => {
  const [{ wallet }] = useConnectWallet();
  const { chain, isBusy, setChain } = useChain();
  const silverPrice = useSilverPrice();
  const [balance, setBalance] = useState<number | undefined>(undefined);
  const [depositAmount, setDepositAmount] = useState<number | null>(null);
  const [canDeposit, setCanDeposit] = useState<boolean>(false);
  const [canWithdraw, setCanWithdraw] = useState<boolean>(false);
  const [ethPrice, setEthPrice] = useState<number | undefined>(undefined);
  const [interestEarned, setInterestEarned] = useState<number | undefined>(
    undefined
  );
  const [interestRate, setInterestRate] = useState<number | undefined>(
    undefined
  );
  const [numRetries, setNumRetries] = useState<number>(MAX_NUM_RETRIES);
  const [withdrawAmount, setWithdrawAmount] = useState<number | null>(null);
  const fetchValues = useCallback(async () => {
    const address = wallet?.accounts[0]?.address;

    if (!address) {
      return;
    }

    const [userDepositInfo, canDeposit, canWithdraw, eth_usd_price] =
      await Promise.all([
        fetch<[bigint, bigint, bigint, bigint]>(
          "treasuryPool",
          "getUserDepositInfo"
        )(chain, address),
        fetch<boolean>("treasuryPool", "canDeposit")(chain),
        fetch<boolean>("treasuryPool", "canWithdraw")(chain),
        fetch<bigint>("xsd", "eth_usd_price")(chain),
      ]);
    const [principalUsd, weightedRate, accumulatedInterest, updatedAt] =
      userDepositInfo ?? [];

    setBalance(
      principalUsd !== undefined ? Number(principalUsd) / 10 ** 18 : undefined
    );
    setCanDeposit(Boolean(canDeposit));
    setCanWithdraw(Boolean(canWithdraw));
    setEthPrice(
      eth_usd_price !== undefined ? Number(eth_usd_price) / 10 ** 6 : undefined
    );
    setInterestEarned(() => {
      if (principalUsd === undefined) {
        return 0;
      }

      if (!updatedAt) {
        return Number(accumulatedInterest ?? 0) / 10 ** 18;
      }

      const now = Math.floor(Date.now() / 1000); // seconds
      const elapsedSeconds = Math.max(now - Number(updatedAt), 0);
      const elapsedDays = Math.max(
        Math.floor(elapsedSeconds / (60 * 60 * 24)),
        0
      );

      return (
        (Number(accumulatedInterest ?? 0) +
          (Number(principalUsd) * Number(weightedRate ?? 0) * elapsedDays) /
            (365 * 10 ** 20)) /
        10 ** 18
      );
    });
    setInterestRate(
      weightedRate !== undefined ? Number(weightedRate) / 10 ** 18 : undefined
    );
  }, [chain, wallet]);
  const maxDepositAmount =
    ethPrice && maxDeposit
      ? (maxDeposit - (currentlyDeposited ?? 0)) / ethPrice
      : undefined;
  const withdrawValue =
    withdrawAmount && silverPrice ? withdrawAmount * silverPrice : undefined;

  useEffect(() => {
    fetchValues();
  }, [fetchValues]);

  useEffect(() => {
    if (numRetries < MAX_NUM_RETRIES) {
      if (canDeposit && canWithdraw) {
        setNumRetries(MAX_NUM_RETRIES);
      } else {
        const timeout = setTimeout(() => {
          setNumRetries((prevState) => prevState + 1);
          fetchValues();
        }, RETRY_INTERVAL);

        return () => clearTimeout(timeout);
      }
    }
  }, [canDeposit, canWithdraw, fetchValues, numRetries]);

  return (
    <form className={styles.root}>
      <button
        className={styles.chain}
        disabled={CHAINS.length < 2 || isBusy}
        onClick={async () => {
          const nextChain = CHAINS[CHAINS.indexOf(chain) + 1] ?? CHAINS[0];

          if (wallet) {
            await setChain({ chainId: `0x${nextChain.id.toString(16)}` });
          }
        }}
        type="button">
        <chain.Icon className={styles.icon} />
        {chain.title}
      </button>
      <fieldset className={styles.fieldset}>
        <div className={styles.input}>
          <input
            disabled={!canDeposit}
            max={maxDepositAmount}
            min={0}
            onChange={({ target: { value } }) => {
              setDepositAmount(value.trim() === "" ? null : Number(value));
            }}
            placeholder="Amount of aETH"
            type="number"
            value={depositAmount ?? ""}
          />
          <p>
            {ethPrice && depositAmount !== undefined
              ? `USD Value: ${currency((depositAmount ?? 0) * ethPrice)}`
              : null}
          </p>
        </div>
        <Button
          className={styles.button}
          disabled={
            !canDeposit ||
            !depositAmount ||
            (maxDepositAmount !== undefined && depositAmount > maxDepositAmount)
          }
          onClick={async () => {
            if (depositAmount) {
              try {
                await transaction(
                  wallet,
                  "treasuryPool",
                  "deposit"
                )(chain, ethers.parseEther(depositAmount.toString()));
                await transaction(
                  wallet,
                  "treasuryPool",
                  "calculations"
                )(chain);
                await fetchValues();

                setDepositAmount(null);
                setNumRetries(0);
              } catch (error) {
                console.error(error);
              }
            }
          }}>
          Deposit
        </Button>
      </fieldset>
      <dl className={styles.dl}>
        <div>
          <dt>USD Balance</dt>
          <dd>{currency(balance)}</dd>
        </div>
        <div>
          <dt>Average Interest Rate</dt>
          <dd>{percentage(interestRate)}</dd>
        </div>
        <div>
          <dt>Interest Earned To Date</dt>
          <dd>{currency(interestEarned)}</dd>
        </div>
      </dl>
      <fieldset className={styles.fieldset}>
        <div className={styles.input}>
          <input
            disabled={!canWithdraw || !balance}
            max={balance}
            min={0}
            onChange={({ target: { value } }) => {
              setWithdrawAmount(value.trim() === "" ? null : Number(value));
            }}
            placeholder="Amount of XSD"
            type="number"
            value={withdrawAmount ?? ""}
          />
          <p>
            {silverPrice && withdrawAmount
              ? `USD Value: ${currency(withdrawAmount * silverPrice)}`
              : " "}
          </p>
        </div>
        <Button
          className={styles.button}
          disabled={
            !canWithdraw ||
            !silverPrice ||
            !balance ||
            !withdrawAmount ||
            withdrawAmount * silverPrice > balance
          }
          onClick={async () => {
            if (withdrawValue) {
              try {
                await transaction(
                  wallet,
                  "treasuryPool",
                  "withdraw"
                )(chain, ethers.parseEther(withdrawValue.toString()));
                await transaction(
                  wallet,
                  "treasuryPool",
                  "calculations"
                )(chain);
                await fetchValues();

                setWithdrawAmount(null);
                setNumRetries(0);
              } catch (error) {
                console.error(error);
              }
            }
          }}>
          Withdraw
        </Button>
      </fieldset>
      <p className={styles.p}>
        XSD = 1 gram of silver = {currency(silverPrice)}
      </p>
    </form>
  );
};
